// https://en.wikipedia.org/wiki/ANSI_escape_code#Colors
const nodeColorsOb = {
  black: '\x1b[30m',
  red: '\x1b[31m',
  green: '\x1b[32m',
  yellow: '\x1b[33m',
  blue: '\x1b[34m',
  magenta: '\x1b[35m',
  cyan: '\x1b[36m',
  white: '\x1b[37m',
};

type NodeColor = keyof typeof nodeColorsOb;

interface LogOptions {
  color?: NodeColor | string;
  enabled?: boolean;
  isBrowser?: boolean;
  logFilter?: RegExp | string;
  trace?: boolean;
  truncateObjects?: boolean | number | string;
}

export interface Logger {
  (...restArgs: unknown[]): void;
  info: Logger;
  warn: Logger;
  detailed: Logger;
  error: Logger;
  setEnabled: (enabled: boolean) => void;
  sublog: (sublogName: string, sublogOptions?: Partial<LogOptions>) => Logger;
}

function clog(name: string, options: LogOptions = {}): Logger {
  let prefix = name + ': ';
  let style: string | null = null;

  if (options.color) {
    if (options.isBrowser) {
      prefix = '%c' + prefix;
      style = `color: ${options.color}`;
    } else {
      const cstring = nodeColorsOb[options.color as NodeColor];
      if (cstring) {
        prefix = cstring + prefix;
      }
    }
  }

  const trunc = (str: string, len: number) => (str.length > len ? str.substring(0, len) + '...' : str);
  const checkPassesFilter = (args: unknown[], filter: RegExp | string) => {
    const firstArg = String(args[0] ?? '');

    return typeof filter === 'string' ? firstArg.includes(filter) : filter.test(firstArg);
  };

  function logGen(cmethod: (...args: unknown[]) => void): Logger {
    const logMethod = function (...restArgs: unknown[]) {
      const isEnabled = options.enabled === undefined ? true : !!options.enabled;
      if (isEnabled || cmethod === console.error) {
        let args = [...restArgs];
        if (style) {
          args.unshift(style);
        }
        args.unshift(prefix);
        if (options.truncateObjects && cmethod !== console.error) {
          args = args.map(arg => {
            if (typeof arg === 'object') {
              return trunc(JSON.stringify(arg), parseInt(String(options.truncateObjects), 10) || 100);
            }
            return arg;
          });
        }
        const passesFilter = !options.logFilter || checkPassesFilter(args, options.logFilter);
        if (passesFilter || cmethod === console.error) {
          cmethod.apply(console, args);
        }
      }
    };

    return logMethod as Logger;
  }

  const log = logGen(options.trace ? console.trace : console.log);
  log.info = log;
  log.warn = logGen(console.warn);
  log.detailed = logGen(console.debug);
  log.error = logGen(console.error);

  log.setEnabled = (e: boolean) => {
    options.enabled = e;
  };

  return log;
}

export function initLog(name: string, options: LogOptions = {}): Logger {
  const log = clog(name, options);
  log.sublog = (sublogName: string, sublogOptions: Partial<LogOptions> = {}) =>
    initLog(name + '.' + sublogName, Object.assign(Object.create(options), sublogOptions));
  return log;
}
